/**
 * TokenUsagePanel — VP-256. Zakładka „Zużycie tokenów": każde zdarzenie, które
 * ruszyło pulę — data z godziną, narzędzie, ile zeszło i ile zostało.
 *
 * To ledger rozliczeniowy, więc doklejamy kolejne strony zamiast je podmieniać:
 * klient przewija historię w dół, a nie skacze po numerach stron.
 *
 * Strony trzyma `useInfiniteQuery`, a nie stan komponentu. Zakładki ustawień
 * renderują się warunkowo, więc panel odmontowuje się przy każdym przełączeniu —
 * lista w `useState` znikałaby, a `staleTime: Infinity` z `queryClient` nie
 * pozwoliłby jej odbudować (zapytanie nie jest ponawiane, bo cache jest świeży).
 */

import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { resolveModuleLabel } from "@/lib/module-labels";
import { Coins, Loader2, Receipt } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type UsageKind = "charge" | "refund" | "failed";

type UsageEntry = {
  id: string;
  occurredAt: string;
  moduleId: string | null;
  operation: string | null;
  tokens: number;
  balanceAfter: number;
  kind: UsageKind;
};

type UsageResponse = {
  entries: UsageEntry[];
  nextCursor: string | null;
  /** Backend liczy podsumowanie tylko dla pierwszej strony. */
  summary: { since: string; usedTokens: number } | null;
};

const KIND_CLASSES: Record<UsageKind, string> = {
  charge: "text-white",
  refund: "text-green-400",
  failed: "text-orange-400",
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Znak zależy od kierunku: pobranie na minus, zwrot na plus. Zdarzenie zerowe
 * (na prodzie kilkadziesiąt takich) zostaje bez znaku — „+0" czytałoby się jak zwrot.
 */
function formatTokenDelta(tokens: number): string {
  const value = Math.abs(tokens).toLocaleString("pl-PL");
  if (tokens === 0) return value;
  return `${tokens > 0 ? "−" : "+"}${value}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function TokenUsagePanel() {
  const { t } = useTranslation("billingPanel");
  const { t: tRoot } = useTranslation();

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<UsageResponse>({
      queryKey: ["/api/billing/token-usage"],
      queryFn: ({ pageParam }) => {
        const params = new URLSearchParams();
        if (pageParam) params.set("cursor", String(pageParam));
        const query = params.toString();
        return apiRequest("GET", `/api/billing/token-usage${query ? `?${query}` : ""}`);
      },
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    });

  const entries = data?.pages.flatMap((page) => page.entries) ?? [];
  // Podsumowanie przychodzi wyłącznie z pierwszej strony i nie zmienia się przy
  // doczytywaniu starszych zdarzeń.
  const summary = data?.pages[0]?.summary ?? null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-white/40">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-8 text-center">
        <p className="text-white/50 text-sm">{t("usage.error")}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-10 text-center">
        <Receipt className="w-6 h-6 text-white/20 mx-auto mb-3" />
        <p className="text-white/50 text-sm">{t("usage.empty")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-white text-sm font-medium">{t("usage.title")}</h3>
          <p className="text-white/40 text-xs mt-1">{t("usage.subtitle")}</p>
        </div>
        {summary && (
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2">
            <div className="flex items-center gap-1.5 text-white/40 text-[11px]">
              <Coins className="w-3 h-3 text-[#ef3e29]" />
              {t("usage.sinceLabel", { date: formatDate(summary.since) })}
            </div>
            <p className="text-white text-sm font-medium mt-0.5">
              {t("usage.usedTokens", { count: summary.usedTokens })}
            </p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("usage.columnWhen")}</TableHead>
              <TableHead>{t("usage.columnTool")}</TableHead>
              <TableHead className="text-right">{t("usage.columnTokens")}</TableHead>
              <TableHead className="text-right">{t("usage.columnBalance")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="text-white/60 whitespace-nowrap">
                  {formatDateTime(entry.occurredAt)}
                </TableCell>
                <TableCell>
                  <span className="text-white">
                    {resolveModuleLabel(entry.moduleId, tRoot, t("usage.unknownTool"))}
                  </span>
                  {entry.operation && (
                    <span className="block text-white/30 text-[11px] font-mono">
                      {entry.operation}
                    </span>
                  )}
                  {entry.kind === "failed" && (
                    <span className="block text-orange-400/70 text-[11px]">
                      {t("usage.failedHint")}
                    </span>
                  )}
                </TableCell>
                <TableCell className={`text-right font-medium ${KIND_CLASSES[entry.kind]}`}>
                  {formatTokenDelta(entry.tokens)}
                </TableCell>
                <TableCell className="text-right text-white/50">
                  {entry.balanceAfter.toLocaleString("pl-PL")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {hasNextPage && (
        <button
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] hover:border-white/[0.16] disabled:opacity-30 disabled:cursor-not-allowed text-white/60 text-xs py-2 transition-colors flex items-center justify-center gap-1.5"
        >
          {isFetchingNextPage && <Loader2 className="w-3 h-3 animate-spin" />}
          {t("usage.loadMore")}
        </button>
      )}
    </div>
  );
}
